/**
 * The onboarding journeys — one ordered declaration per persona, and the only
 * place a rung of either journey is written down.
 *
 * A journey is its STAGES in forward order. Everything the engine knows about
 * the vocabulary is computed from these two arrays in `./derived`: the entity
 * status enums, the user step enum and its ordinals, the labels, the
 * status→step floors, the fan-out gate, the portal route tables, the admin
 * stepper tables, the step→status map. Those lived in a dozen hand-written
 * copies with no compiler link between them, so agreement rested on review
 * alone — and a copy drifting from its twin is how a paying builder stranded.
 *
 * Not every declared rung is on every subject's path: `enabledBy` puts one
 * behind a platform switch and `appliesWhen` behind a branch predicate. A rung
 * that does not apply stays DECLARED — with its status, its step and its route
 * intact, so anyone already standing on it still resolves and routes — and the
 * engine simply resolves past it when deciding where someone goes next. See
 * `../applicability`.
 *
 * Pure and client-safe: no database, auth, or side-effectful imports (the two
 * imports below are types, erased at compile).
 */

import type { JourneyFacts } from '../applicability';
import type { OnboardingFlagKey } from '../flags';

/**
 * Whether a stage is a personal attestation or a company fact — THE single
 * encoding of a split four places used to hand-maintain (the fan-out gate, the
 * reconciler's early return, and both status→step floor maps each named the
 * same two steps by hand).
 *
 * 'user' stages are attestations: a teammate signing does not sign for me, so
 * account-level progress must never imply or skip one. 'entity' stages are
 * company facts — one person's action discharges them for everyone, and a user
 * behind their account is carried forward to them.
 */
export type JourneyLane = 'entity' | 'user';

/**
 * Who may discharge a stage.
 *
 * `self` is any of the company's own users; a rung reserved for one kind of
 * customer names that role instead. `specialist` is an internal role acting on
 * the account's behalf. Narrowing a rung is therefore a one-line edit to its
 * declaration, and every surface that acts on the rung asks `../discharge` —
 * so the excluded role is routed to a wait state rather than to a dead end.
 */
export type StageDischarger = 'self' | 'specialist' | 'customer_admin' | 'customer_user';

export interface JourneyStage {
  /** Stable, opaque handle for this rung; unique within its journey. */
  readonly id: string;
  /** Human name for the stage — the entity-status vocabulary. */
  readonly label: string;
  readonly lane: JourneyLane;
  readonly dischargeableBy: readonly StageDischarger[];
  /** The stored values this stage is written as. A stage may carry an entity
   *  status, a user step, or both; a branch rung carries only a step, and the
   *  origin only a status. */
  readonly legacy: {
    readonly statusValue?: string;
    readonly userStep?: string;
    /** Historic step spellings accepted on read, never written. */
    readonly aliases?: readonly string[];
  };
  /** Platform switch this stage hangs on. Absent = always part of the journey.
   *  When the switch is off (or the subject predates its cutoff) the stage is
   *  skipped: nobody new is sent to it, and a transition aimed at it lands on
   *  the next rung that applies. */
  readonly enabledBy?: OnboardingFlagKey;
  /** Branch predicate over the subject's facts. Absent = applies to everyone.
   *  For rungs that belong to one product line rather than to the persona. */
  readonly appliesWhen?: (facts: JourneyFacts) => boolean;
  /** Human name for what the branch predicate selects. A predicate cannot
   *  render itself, so a rung that carries one carries this too — otherwise
   *  every surface that shows the journey has to say "some subjects" about a
   *  rung whose audience is a decided, nameable thing. */
  readonly branchLabel?: string;
  /** The portal is NOT pinned to this stage's page — the user may navigate
   *  freely. Declared per stage rather than as a threshold, so a rung that
   *  roams need not be preceded only by rungs that also do. */
  readonly roam: boolean;
  /** Where the portal sends a user sitting on this stage. Per-journey because
   *  the same rung routes to a different persona's page. Absent only where no
   *  user step names the stage — an entity fact that predates any user. */
  readonly route?: string;
  /** How the admin stepper renders this stage; present on exactly the stages
   *  that have an entity status, since that is what the stepper walks. `label`
   *  is deliberately terser than the stage's own. `adminJumpStep` is the step
   *  an admin jumps to, and its presence IS the stage's jumpability: only the
   *  specialist-driven rungs carry one, because the rest are driven by user
   *  action (signing, paying) and must not be bypassed in normal flow. */
  readonly stepper?: {
    readonly label: string;
    readonly description: string;
    readonly adminJumpStep?: string;
  };
}

/**
 * Builder journey. The stepper covers onboarding progression only; account
 * stops (suspended / archived) live on the orthogonal lifecycle `status` axis,
 * never here.
 */
export const BUILDER_STANDARD = [
  {
    id: 'unclaimed',
    label: 'Unclaimed',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'unclaimed' },
    roam: false,
    stepper: {
      label: 'Unclaimed',
      description: 'Builder record created but no invitation sent yet.',
    },
  },
  {
    id: 'legal_agreements',
    label: 'Invitation Sent',
    lane: 'user',
    dischargeableBy: ['self'],
    legacy: { statusValue: 'invitation_sent', userStep: 'legal_agreements' },
    roam: false,
    route: '/onboarding/legal-agreements',
    stepper: {
      label: 'Invitation Sent',
      description: 'Invite email delivered. Waiting for the builder to accept.',
    },
  },
  {
    id: 'cnda',
    label: 'CNDA Pending',
    lane: 'user',
    dischargeableBy: ['self'],
    legacy: { statusValue: 'cnda_pending', userStep: 'cnda' },
    enabledBy: 'onboarding.individual_cnda_required',
    roam: false,
    route: '/onboarding/confidentiality-agreement',
    stepper: {
      label: 'CNDA',
      description: 'Builder is reviewing and signing the CNDA.',
    },
  },
  {
    // The mutual NDA is a COMPANY agreement, not a personal attestation: one
    // person signing discharges it for everyone at that company, which is what
    // puts it in the entity lane. Either kind of evidence satisfies it — an
    // in-portal signature recorded as an acceptance bound to the company, or an
    // approved MNDA document executed outside the portal and uploaded by a
    // specialist — and an executed document is never invalidated by a later
    // template version. Behind a switch, and (being entity-lane) behind that
    // switch's cutoff, so turning the requirement on never demands it of
    // companies that onboarded before the decision.
    id: 'mnda',
    label: 'MNDA Pending',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'mnda_pending', userStep: 'mnda' },
    enabledBy: 'onboarding.mnda_required',
    roam: false,
    route: '/onboarding/mutual-nda',
    stepper: {
      label: 'MNDA',
      description: 'Mutual NDA executed for the company',
      adminJumpStep: 'mnda',
    },
  },
  {
    id: 'payment',
    label: 'Payment Pending',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'payment_pending', userStep: 'payment' },
    roam: false,
    route: '/onboarding/payment',
    stepper: {
      label: 'Payment',
      description: 'Builder is completing the subscription payment.',
      adminJumpStep: 'payment',
    },
  },
  {
    id: 'phase1_documents',
    label: 'Phase 1 Documents',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: {
      statusValue: 'phase1_documents_pending',
      userStep: 'phase1_documents',
      // Historic spellings of THIS rung. `documents` is the pre-phase-split
      // name. `phase1_review` was a separate waiting-for-review rung with no
      // status of its own; waiting for review is a computed substate of the
      // document phase, so the rung collapsed back into it and its stored
      // spelling now resolves here — forward for the roaming it loses, never
      // backward. Stored rows are NOT healed by this and the database CHECK
      // still accepts the old spellings; narrowing either is its own change.
      aliases: ['documents', 'phase1_review'],
    },
    roam: false,
    route: '/portal/builder/documents',
    stepper: {
      label: 'Phase 1 Documents',
      description: 'Core compliance documents (COI, W-9, etc.) being uploaded and reviewed.',
      adminJumpStep: 'phase1_documents',
    },
  },
  {
    id: 'phase2_documents',
    label: 'Phase 2 Documents',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: {
      statusValue: 'phase2_documents_pending',
      userStep: 'phase2_documents',
      // See phase1_documents: the phase-2 waiting-for-review rung collapsed
      // into the phase it waits on.
      aliases: ['phase2_review'],
    },
    roam: false,
    route: '/portal/builder/subcontractor-documents',
    stepper: {
      label: 'Phase 2 Documents',
      description: 'Subcontractor compliance rolling up. Finalizing the builder onboarding.',
      adminJumpStep: 'phase2_documents',
    },
  },
  {
    id: 'complete',
    label: 'Onboarded',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'onboarded', userStep: 'complete' },
    roam: true,
    route: '/portal/builder/dashboard',
    stepper: {
      label: 'Onboarded',
      description: 'Builder fully onboarded. Compliance monitoring continues.',
      adminJumpStep: 'complete',
    },
  },
] as const satisfies readonly JourneyStage[];

/**
 * Subcontractor journey. No phase split — one documents rung covers every
 * compliance upload, and there is no second document phase behind it.
 */
export const SUBCONTRACTOR_STANDARD = [
  {
    id: 'unclaimed',
    label: 'Unclaimed',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'unclaimed' },
    roam: false,
    stepper: {
      label: 'Unclaimed',
      description: 'Subcontractor record created but no invitation sent yet.',
    },
  },
  {
    id: 'legal_agreements',
    label: 'Invitation Sent',
    lane: 'user',
    dischargeableBy: ['self'],
    legacy: { statusValue: 'invitation_sent', userStep: 'legal_agreements' },
    roam: false,
    route: '/onboarding/legal-agreements',
    stepper: {
      label: 'Invitation Sent',
      description: 'Invite email delivered. Waiting for the subcontractor to accept.',
    },
  },
  {
    id: 'cnda',
    label: 'CNDA Pending',
    lane: 'user',
    dischargeableBy: ['self'],
    legacy: { statusValue: 'cnda_pending', userStep: 'cnda' },
    enabledBy: 'onboarding.individual_cnda_required',
    roam: false,
    route: '/onboarding/confidentiality-agreement',
    stepper: {
      label: 'CNDA',
      description: 'Subcontractor is reviewing and signing the CNDA.',
    },
  },
  {
    // See the builder journey's rung of the same name: a company agreement in
    // the entity lane, satisfied by an in-portal signature bound to the company
    // or by an approved uploaded MNDA, behind a switch and its cutoff. The
    // subcontractor's rung sits between the CNDA and their documents, because
    // the payment rung after it belongs to one premium line rather than to the
    // persona.
    id: 'mnda',
    label: 'MNDA Pending',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'mnda_pending', userStep: 'mnda' },
    enabledBy: 'onboarding.mnda_required',
    roam: false,
    route: '/onboarding/mutual-nda',
    stepper: {
      label: 'MNDA',
      description: 'Mutual NDA executed for the company',
      adminJumpStep: 'mnda',
    },
  },
  {
    // The Subcontractor Shield premium line's rung: subs on that service model
    // pay, and every other sub goes straight from the CNDA to their documents.
    // The branch is expressed here so the journey states it once — no column
    // records a service model yet, so the predicate is false for everyone and
    // the rung is skipped. It carries no entity status either way: the payment
    // would be a user position, and the sub's company status is unaffected.
    id: 'payment',
    label: 'Payment',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { userStep: 'payment' },
    appliesWhen: (facts: JourneyFacts) => facts.entity?.serviceModel === 'shield',
    branchLabel: 'Subcontractor Shield service model',
    roam: false,
    route: '/onboarding/payment',
  },
  {
    id: 'phase1_documents',
    label: 'Documents Pending',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: {
      statusValue: 'documents_pending',
      userStep: 'phase1_documents',
      // See the builder's document rung for both spellings: `documents` is the
      // pre-phase-split name, and `phase1_review` was the waiting-for-review
      // rung that collapsed into the phase it waits on.
      aliases: ['documents', 'phase1_review'],
    },
    roam: false,
    route: '/portal/subcontractor/documents',
    stepper: {
      label: 'Documents',
      description: 'Compliance documents (COI, W-9, license, etc.) being uploaded and reviewed.',
      // The alias, not the canonical step: the sub stepper has jumped by this
      // spelling since before the canonical name existed.
      adminJumpStep: 'documents',
    },
  },
  {
    id: 'complete',
    label: 'Onboarded',
    lane: 'entity',
    dischargeableBy: ['self', 'specialist'],
    legacy: { statusValue: 'onboarded', userStep: 'complete' },
    roam: true,
    route: '/portal/subcontractor/dashboard',
    stepper: {
      label: 'Onboarded',
      description: 'Subcontractor fully onboarded. Compliance monitoring continues.',
      adminJumpStep: 'complete',
    },
  },
] as const satisfies readonly JourneyStage[];

/** Both journeys, for the derivations that span personas (the user step enum,
 *  its ordinals, the labels, the lane sets). */
export const ALL_JOURNEYS: readonly (readonly JourneyStage[])[] = [
  BUILDER_STANDARD,
  SUBCONTRACTOR_STANDARD,
];
